import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import { relative } from "@/lib/format";
import { Inbox } from "lucide-react";
import { toast } from "sonner";

export default function ProfileRequests() {
  const qc = useQueryClient();
  const { me } = useApp();
  const [search, setSearch] = useState("");
  const { data: requests, isLoading } = useQuery({ queryKey: ["profile-requests"], queryFn: () => get("/hrms/profile-requests?status=pending") });

  const decide = useMutation({
    mutationFn: ({ id, action }) => post(`/hrms/profile-requests/${id}/decision`, { action, _actor: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["profile-requests"] }); toast.success("Updated"); },
  });

  const filteredRequests = useMemo(() => {
    return (requests || []).filter((r) => {
      if (search && !r.employee_name?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [requests, search]);

  return (
    <div>
      <PageHeader title="Profile Change Requests" subtitle="Employee requests to change locked fields, awaiting HR approval." />
      {!isLoading && requests?.length > 0 && (
        <div className="mb-4">
          <SearchInput testid="profile-requests-search" placeholder="Search employee name" value={search} onChange={setSearch} />
        </div>
      )}
      {isLoading ? <Skeleton className="h-64" /> : !requests?.length ? (
        <EmptyState icon={Inbox} title="No pending requests" />
      ) : !filteredRequests.length ? (
        <EmptyState icon={Inbox} title="No matching requests" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="divide-y divide-border">
            {filteredRequests.map((r) => (
              <div key={r.id} data-testid={`profile-request-${r.id}`} className="flex items-center justify-between px-4 py-3 text-sm">
                <div>
                  <div className="font-medium">{r.employee_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {Object.entries(r.requested_fields).map(([k, v]) => `${k.replace("_", " ")} → ${v}`).join(", ")}
                  </div>
                  <div className="text-xs text-muted-foreground">Requested {relative(r.requested_at)}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => decide.mutate({ id: r.id, action: "reject" })}>Reject</Button>
                  <Button size="sm" onClick={() => decide.mutate({ id: r.id, action: "approve" })}>Approve</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
